import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useParams, useSearchParams} from 'react-router-dom';

import {CPaymentLinkStatuses} from '@/app/main/paymentLink/models/constants';
import {
  IPaymentLinkV2ValidProps,
  TPaymentLinkStatus,
} from '@/app/main/paymentLink/v2/pages/paymentLinkV2/models/interfaces/paymentLinkV2.interfaces';
import {HTTP_ERROR, THttpErrorCode} from '@/constants/HttpErrors';
import {useRemoteFeatureFlags} from '@/dataSource/src/api/firebase/useRemoteFeatureFlags';
import {mapPaymentLinkToValidProps} from '@/dataSource/src/api/payment-link/v2/mapper';
import {usePaymentLinkV2Repository} from '@/dataSource/src/api/payment-link/v2/useRepository';

import {PAYMENT_LINK_V2_QUERY_PARAMS} from './models/constants/constants';

type PaymentLinkV2ContextType = {
  status: TPaymentLinkStatus | null;
  validLinkProps: IPaymentLinkV2ValidProps;
  receiverName?: string;
  paymentLinkId: string;
};

const PaymentLinkV2Context = createContext<PaymentLinkV2ContextType | null>(
  null,
);

export const PaymentLinkV2ViewModelProvider: React.FC<
  React.PropsWithChildren
> = ({children}) => {
  const {id: paymentLinkId} = useParams<{id: string}>();

  const [searchParams] = useSearchParams();

  const baseAmount = useMemo<number | null>(() => {
    const value = searchParams.get(PAYMENT_LINK_V2_QUERY_PARAMS.BASE_AMOUNT);
    if (value === null) return null;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, [searchParams]);

  const isFundRaise = useMemo(
    () => searchParams.get(PAYMENT_LINK_V2_QUERY_PARAMS.FUND_RAISE) !== null,
    [searchParams],
  );

  const {handlerGetPaymentLinkById} = usePaymentLinkV2Repository();
  const {isLoading: isFlagsLoading} = useRemoteFeatureFlags();

  const [status, setStatus] = useState<TPaymentLinkStatus>(
    CPaymentLinkStatuses.LOADING,
  );
  const [receiverName, setReceiverName] = useState<string>('the sender');
  const [validLinkProps, setValidLinkProps] =
    useState<IPaymentLinkV2ValidProps>({
      paymentLinkId,
      receiverUserId: '',
      name: '',
      gif: '',
      note: '',
      amount: baseAmount,
    });

  const mapStatusFromCode = useCallback((code?: number): TPaymentLinkStatus => {
    switch (code as THttpErrorCode) {
      case HTTP_ERROR.NOT_FOUND:
        return CPaymentLinkStatuses.NOT_VALID;
      case HTTP_ERROR.BAD_REQUEST:
        return CPaymentLinkStatuses.USED;
      default:
        return CPaymentLinkStatuses.ERROR;
    }
  }, []);

  const handleLinkStatus = useCallback(async () => {
    if (!paymentLinkId) {
      setStatus(CPaymentLinkStatuses.NOT_VALID);
      return;
    }

    try {
      const resp = await handlerGetPaymentLinkById(
        paymentLinkId,
        isFundRaise ? PAYMENT_LINK_V2_QUERY_PARAMS.FUND_RAISE : undefined,
      );
      if (!resp) {
        setStatus(CPaymentLinkStatuses.ERROR);
        return;
      }
      const mapped = mapPaymentLinkToValidProps(paymentLinkId, resp);
      if (baseAmount !== null && baseAmount > 0) {
        setValidLinkProps({
          ...mapped,
          amount: baseAmount,
        });
      } else {
        setValidLinkProps(mapped);
      }
      setReceiverName(mapped?.name);
      if (!mapped.link_consumed) setStatus(CPaymentLinkStatuses.VALID);
      else setStatus(CPaymentLinkStatuses.USED);
    } catch (err: any) {
      const code = err?.status || err?.originalStatus || err?.data?.status;
      setStatus(mapStatusFromCode(code));
    }
  }, [
    paymentLinkId,
    handlerGetPaymentLinkById,
    baseAmount,
    isFundRaise,
    mapStatusFromCode,
  ]);

  useEffect(() => {
    handleLinkStatus();
  }, [handleLinkStatus]);

  const effectiveStatus = isFlagsLoading
    ? CPaymentLinkStatuses.LOADING
    : status;

  const value = useMemo(
    () => ({
      status: effectiveStatus,
      validLinkProps,
      receiverName,
      paymentLinkId,
      baseAmount,
    }),
    [effectiveStatus, validLinkProps, receiverName, paymentLinkId, baseAmount],
  );

  return (
    <PaymentLinkV2Context.Provider value={value}>
      {children}
    </PaymentLinkV2Context.Provider>
  );
};

export const usePaymentLinkV2ViewModel = () => {
  const ctx = useContext(PaymentLinkV2Context);
  if (!ctx) {
    throw new Error(
      'usePaymentLinkV2ViewModel must be used within PaymentLinkV2ViewModelProvider',
    );
  }
  return ctx;
};
